import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Chip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme,
  Paper,
  SwipeableDrawer,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  MarkEmailRead as MarkEmailReadIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

// Helper function to get notification icon
const getNotificationIcon = (type) => {
  switch (type) {
    case 'new_admin_application':
      return <PersonAddIcon />;
    case 'role_changed':
      return <AdminIcon />;
    default:
      return <InfoIcon />;
  }
};

// Helper function to get notification color
const getNotificationColor = (type) => {
  switch (type) {
    case 'new_admin_application':
      return '#ff9800';
    case 'role_changed':
      return '#2196f3';
    default:
      return '#9e9e9e';
  }
};

const NotificationDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { currentUser, isSuperAdmin, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Check if user is admin (either super admin or regular admin)
  const isAnyAdmin = isSuperAdmin || isAdmin;

  // Fetch notifications - Both admin and super admin see ALL notifications
  useEffect(() => {
    if (!currentUser || !isAnyAdmin || !open) return;

    setLoading(true);
    
    // Both admin and super admin get all notifications
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notificationsData = [];
        snapshot.forEach((doc) => {
          notificationsData.push({ id: doc.id, ...doc.data() });
        });
        setNotifications(notificationsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
        setLoading(false);
        setSnackbar({ open: true, message: 'Failed to load notifications', severity: 'error' });
      }
    );

    return () => unsubscribe();
  }, [currentUser, open, isAnyAdmin]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
      });
    } catch (error) {
      console.error('Error marking as read:', error);
      setSnackbar({ open: true, message: 'Failed to mark as read', severity: 'error' });
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      const unreadNotifications = notifications.filter(n => !n.read);
      
      unreadNotifications.forEach((notification) => {
        const ref = doc(db, 'notifications', notification.id);
        batch.update(ref, { read: true });
      });
      
      await batch.commit();
      setSnackbar({ open: true, message: 'All notifications marked as read', severity: 'success' });
    } catch (error) {
      console.error('Error marking all as read:', error);
      setSnackbar({ open: true, message: 'Failed to mark all as read', severity: 'error' });
    }
  };

  // Delete notification - Both admin and super admin can delete
  const deleteNotification = async (notificationId) => {
    if (!isAnyAdmin) {
      setSnackbar({ open: true, message: 'Only admins can delete notifications', severity: 'error' });
      return;
    }

    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      setSnackbar({ open: true, message: 'Notification deleted', severity: 'success' });
      setAnchorEl(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
      setSnackbar({ open: true, message: 'Failed to delete notification', severity: 'error' });
    }
  };

  // Navigate to approvals page
  const navigateToApprovals = () => {
    onClose();
    navigate('/admin/approvals');
  };

  const getUnreadCount = () => {
    return notifications.filter(n => !n.read).length;
  };

  const getFilteredNotifications = () => {
    if (activeTab === 0) return notifications;
    if (activeTab === 1) return notifications.filter(n => !n.read);
    return notifications;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationsIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Notifications
            </Typography>
            {getUnreadCount() > 0 && (
              <Chip
                label={getUnreadCount()}
                size="small"
                color="primary"
                sx={{ fontWeight: 600, height: 24 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {getUnreadCount() > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton onClick={markAllAsRead} size="small" sx={{ borderRadius: 2 }}>
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small" sx={{ borderRadius: 2 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3,
            },
          }}
        >
          <Tab label="All" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} />
          <Tab label="Unread" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }} />
        </Tabs>
      </Box>

      {/* Notifications List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress size={40} />
          </Box>
        ) : getFilteredNotifications().length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 300,
              color: theme.palette.text.secondary,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <NotificationsIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            </Box>
            <Typography variant="body1" fontWeight={500}>
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              You're all caught up!
            </Typography>
          </Box>
        ) : (
          <AnimatePresence>
            <List sx={{ p: 0 }}>
              {getFilteredNotifications().map((notification, index) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: Math.min(index * 0.03, 0.3) }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      bgcolor: notification.read 
                        ? 'transparent' 
                        : alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${notification.read 
                        ? theme.palette.divider 
                        : alpha(theme.palette.primary.main, 0.15)}`,
                      borderRadius: 2.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        transform: 'translateX(2px)',
                      },
                    }}
                  >
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        py: 1.5,
                        px: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                      }}
                      onClick={() => {
                        if (!notification.read) {
                          markAsRead(notification.id);
                        }
                        // If it's a new admin application, navigate to approvals
                        if (notification.type === 'new_admin_application' && isAnyAdmin) {
                          navigateToApprovals();
                        }
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 48 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(getNotificationColor(notification.type), 0.12),
                            color: getNotificationColor(notification.type),
                            width: 40,
                            height: 40,
                          }}
                        >
                          {getNotificationIcon(notification.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={notification.read ? 500 : 600}
                              sx={{
                                color: notification.read ? 'text.secondary' : 'text.primary',
                                lineHeight: 1.4,
                                flex: 1,
                              }}
                            >
                              {notification.message}
                            </Typography>
                            {!notification.read && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.primary.main,
                                  mt: 0.5,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {formatDate(notification.createdAt)}
                            </Typography>
                            {notification.type === 'new_admin_application' && isAnyAdmin && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigateToApprovals();
                                }}
                                sx={{
                                  mt: 1,
                                  p: 0.5,
                                  textTransform: 'none',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    bgcolor: 'transparent',
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                View in Approvals →
                              </Button>
                            )}
                          </Box>
                        }
                      />
                      {/* Both admin and super admin can see and use the menu */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          setSelectedNotification(notification);
                        }}
                        sx={{ alignSelf: 'flex-start', mt: -0.5, mr: -0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  </Paper>
                </motion.div>
              ))}
            </List>
          </AnimatePresence>
        )}
      </Box>
    </Box>
  );

  // iOS-style bottom drawer for mobile, side drawer for desktop
  if (isMobile) {
    return (
      <>
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          onOpen={() => {}}
          disableSwipeToOpen={false}
          sx={{
            '& .MuiDrawer-paper': {
              height: '80vh',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              overflow: 'hidden',
            },
          }}
        >
          {/* Drag handle for iOS style */}
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: theme.palette.grey[300],
              borderRadius: 2,
              mx: 'auto',
              my: 1.5,
              cursor: 'pointer',
            }}
            onClick={onClose}
          />
          {drawerContent}
        </SwipeableDrawer>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              minWidth: 160,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          }}
        >
          <MenuItem onClick={() => {
            if (selectedNotification) {
              markAsRead(selectedNotification.id);
              setAnchorEl(null);
            }
          }} sx={{ borderRadius: 1.5, mx: 0.5 }}>
            <MarkEmailReadIcon fontSize="small" sx={{ mr: 1.5 }} />
            Mark as read
          </MenuItem>
          <MenuItem 
            onClick={() => {
              if (selectedNotification) {
                deleteNotification(selectedNotification.id);
              }
            }} 
            sx={{ 
              borderRadius: 1.5, 
              mx: 0.5,
              color: theme.palette.error.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.04),
              },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
            Delete
          </MenuItem>
        </Menu>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ 
              width: '100%', 
              borderRadius: 2.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 480,
            borderRadius: '20px 0 0 20px',
            overflow: 'hidden',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            minWidth: 160,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        <MenuItem onClick={() => {
          if (selectedNotification) {
            markAsRead(selectedNotification.id);
            setAnchorEl(null);
          }
        }} sx={{ borderRadius: 1.5, mx: 0.5 }}>
          <MarkEmailReadIcon fontSize="small" sx={{ mr: 1.5 }} />
          Mark as read
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedNotification) {
              deleteNotification(selectedNotification.id);
            }
          }} 
          sx={{ 
            borderRadius: 1.5, 
            mx: 0.5,
            color: theme.palette.error.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.04),
            },
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          Delete
        </MenuItem>
      </Menu>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%', 
            borderRadius: 2.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// CloseIcon component
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Main Notification Icon Component
const NotificationIcon = ({ color, size = 'medium' }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { currentUser, isAdmin, isSuperAdmin } = useAuth();
  const theme = useTheme();

  // Check if user is admin (either super admin or regular admin)
  const isAnyAdmin = isAdmin || isSuperAdmin;

  // Get unread count in real-time - Both see all unread notifications
  useEffect(() => {
    if (!currentUser || !isAnyAdmin) return;

    // Both admin and super admin see all unread notifications
    const q = query(
      collection(db, 'notifications'),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser, isAnyAdmin]);

  // Don't show for non-admin users
  if (!isAnyAdmin) return null;

  const getSize = () => {
    switch (size) {
      case 'small': return 20;
      case 'large': return 28;
      default: return 24;
    }
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            color: color || theme.palette.text.primary,
            transition: 'all 0.2s ease',
            borderRadius: 2,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              transform: 'scale(1.02)',
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 18,
                minWidth: 18,
                borderRadius: 9,
                fontWeight: 600,
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: getSize() }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <NotificationDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
};

export default NotificationIcon;